import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../Layout/Layout';
import '../styles/Leaderboard.css';
import api from '../utils/apiClient';
import styled from 'styled-components';

// Reuse styled components from the main Leaderboard
const LeaderboardContainer = styled.main`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing[6]};
`;

const PageHeader = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing[8]};
  text-align: center;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fonts.sizes['4xl']};
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  letter-spacing: 1px;
  text-transform: uppercase;
  position: relative;
  display: inline-block;
`;

const WorkshopBadge = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #FF6B6B, #4ECDC4);
  color: white;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  font-weight: 600;
  margin: ${({ theme }) => theme.spacing[4]} auto;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  max-width: 600px;
  margin: 0 auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[16]} 0;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid ${({ theme }) => theme.colors.backgroundLighter};
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  box-shadow: 0 0 15px ${({ theme }) => theme.colors.glow};
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[16]} 0;
  color: ${({ theme }) => theme.colors.error};
`;

const BackButton = styled.button`
  background: ${({ theme }) => theme.colors.backgroundLight};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  
  &:hover {
    background: ${({ theme }) => theme.colors.backgroundLighter};
    transform: translateX(-2px);
  }
`;

const LeaderboardTable = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 220px;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 220px;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  align-items: center;
  transition: all ${({ theme }) => theme.transitions.normal};
  background-color: ${({ theme, isCurrentUser }) =>
    isCurrentUser ? `rgba(10, 255, 112, 0.05)` : 'transparent'};
  position: relative;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeIn 0.5s forwards;
  animation-delay: ${({ index }) => `${index * 0.1}s`};
  cursor: pointer;
  
  &:hover {
    background-color: ${({ theme, isCurrentUser }) =>
      isCurrentUser ? `rgba(10, 255, 112, 0.08)` : theme.colors.backgroundLighter};
    transform: translateX(5px);
    box-shadow: ${({ theme }) => theme.shadows.cardHover};
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  ${({ isCurrentUser, theme }) => isCurrentUser && `
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: ${theme.colors.gradientPrimary};
    }
  `}
  
  @keyframes fadeIn {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 60px 1fr auto;
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  }
`;

const RankBadge = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  font-weight: 600;
  color: ${({ theme, rank }) => {
    if (rank === 1) return theme.colors.primary;
    if (rank === 2) return theme.colors.secondary;
    if (rank === 3) return theme.colors.info;
    return theme.colors.textPrimary;
  }};
  position: relative;
  
  ${({ rank, theme }) => rank <= 3 && `
    &::after {
      content: '';
      position: absolute;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      background: ${rank === 1
        ? `radial-gradient(circle, rgba(10, 255, 112, 0.1) 0%, rgba(10, 255, 112, 0) 70%)`
        : rank === 2
          ? `radial-gradient(circle, rgba(9, 182, 255, 0.1) 0%, rgba(9, 182, 255, 0) 70%)`
          : `radial-gradient(circle, rgba(76, 131, 255, 0.1) 0%, rgba(76, 131, 255, 0) 70%)`};
      z-index: -1;
    }
  `}
`;

const Trophy = styled.span`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  filter: drop-shadow(0 0 3px ${({ rank, theme }) =>
    rank === 1
      ? theme.colors.primary
      : rank === 2
        ? theme.colors.secondary
        : theme.colors.info
  });
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const UserAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.background};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  background: ${({ background }) => background};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: ${({ glowColor }) => glowColor};
    border-radius: inherit;
    z-index: -1;
    opacity: 0.5;
    filter: blur(4px);
  }
  
  &:hover {
    transform: scale(1.05);
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const UserName = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const UserStats = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[1]};
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing[2]};
    justify-content: flex-end;
  }
`;

const StatValue = styled.span`
  font-weight: 600;
  font-size: ${({ theme, isPoints }) => isPoints
    ? theme.fonts.sizes.xl
    : theme.fonts.sizes.lg};
  color: ${({ theme, isPoints }) => isPoints
    ? theme.colors.primary
    : theme.colors.textPrimary};
  text-shadow: ${({ theme, isPoints }) => isPoints
    ? `0 0 10px ${theme.colors.glow}`
    : 'none'};
`;

const StatLabel = styled.span`
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const WorkshopLeaderboard = () => {
  const { workshopCode } = useParams();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchWorkshopLeaderboard = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Call the workshop leaderboard API endpoint
        const response = await fetch(`/api/user/leaderboard/workshops/${workshopCode}/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('You don\'t have access to this workshop leaderboard.');
          } else if (response.status === 404) {
            throw new Error('Workshop not found.');
          } else {
            throw new Error('Failed to load workshop leaderboard.');
          }
        }

        const data = await response.json();
        setLeaderboardData(data);
      } catch (err) {
        console.error('Error fetching workshop leaderboard:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (workshopCode) {
      fetchWorkshopLeaderboard();
    }
  }, [workshopCode]);

  const getInitials = (name) => {
    if (!name) return '';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getTrophy = (rank) => {
    if (rank === 1) return <Trophy rank={rank}>🏆</Trophy>;
    if (rank === 2) return <Trophy rank={rank}>🥈</Trophy>;
    if (rank === 3) return <Trophy rank={rank}>🥉</Trophy>;
    return <span>#{rank}</span>;
  };

  const calculateProgress = (user) => {
    if (leaderboardData.length === 0) return 5;
    const maxPoints = Math.max(...leaderboardData.map(u => u.reward_points));
    return Math.max(5, (user.reward_points / maxPoints) * 100);
  };

  const getAvatarStyle = (userId, rank) => {
    if (rank === 1) {
      return {
        background: 'linear-gradient(135deg, #0AFF70, #06C058)',
        glow: 'rgba(10, 255, 112, 0.6)'
      };
    }
    if (rank === 2) {
      return {
        background: 'linear-gradient(135deg, #09B6FF, #0788C0)',
        glow: 'rgba(9, 182, 255, 0.6)'
      };
    }
    if (rank === 3) {
      return {
        background: 'linear-gradient(135deg, #4C83FF, #3A63C0)',
        glow: 'rgba(76, 131, 255, 0.6)'
      };
    }

    const hue = (userId * 137) % 360;
    return {
      background: `linear-gradient(135deg, hsl(${hue}, 80%, 50%), hsl(${hue + 30}, 70%, 40%))`,
      glow: `hsla(${hue}, 80%, 50%, 0.4)`
    };
  };

  const handleRowClick = (userId) => {
    navigate(`/leaderboard/${userId}`);
  };

  const handleBackClick = () => {
    navigate('/leaderboard');
  };

  if (loading) {
    return (
      <Layout>
        <LeaderboardContainer>
          <LoadingContainer>
            <LoadingSpinner />
            <p>Loading workshop leaderboard...</p>
          </LoadingContainer>
        </LeaderboardContainer>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <LeaderboardContainer>
          <ErrorContainer>
            <h2>Oops! Something went wrong</h2>
            <p>{error}</p>
            <BackButton onClick={handleBackClick}>
              ← Back to Global Leaderboard
            </BackButton>
          </ErrorContainer>
        </LeaderboardContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <LeaderboardContainer>
        <BackButton onClick={handleBackClick}>
          ← Back to Global Leaderboard
        </BackButton>
        
        <PageHeader>
          <Title>Workshop Leaderboard</Title>
          {/* <WorkshopBadge>Workshop {workshopCode}</WorkshopBadge> */}
          <Subtitle>
            Rankings among workshop participants only
          </Subtitle>
        </PageHeader>

        {leaderboardData.length === 0 ? (
          <ErrorContainer>
            <h3>No participants yet</h3>
            <p>This workshop doesn't have any active participants or no one has started learning yet.</p>
          </ErrorContainer>
        ) : (
          <LeaderboardTable>
            <TableHeader>
              <div>Rank</div>
              <div>Participant</div>
              <div>Stats</div>
            </TableHeader>

            {leaderboardData.map((user, index) => {
              const avatarStyle = getAvatarStyle(user.user_id, user.rank);

              return (
                <TableRow
                  key={user.user_id}
                  isCurrentUser={user.is_current_user}
                  index={index}
                  onClick={() => handleRowClick(user.user_id)}
                >
                  <RankBadge rank={user.rank}>
                    {getTrophy(user.rank)}
                  </RankBadge>

                  <UserProfile>
                    {user.profile_image ? (
                      <UserAvatar
                        as="img"
                        src={user.profile_image}
                        alt={user.name}
                        background={avatarStyle.background}
                        glowColor={avatarStyle.glow}
                      />
                    ) : (
                      <UserAvatar
                        background={avatarStyle.background}
                        glowColor={avatarStyle.glow}
                      >
                        {getInitials(user.name)}
                      </UserAvatar>
                    )}
                    <UserInfo>
                      <UserName>{user.name}</UserName>
                    </UserInfo>
                  </UserProfile>

                  <UserStats>
                    <StatItem>
                      <StatValue isPoints>{user.reward_points}</StatValue>
                      <StatLabel>XP</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{user.completed_courses}</StatValue>
                      <StatLabel>Courses</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{user.completed_modules}</StatValue>
                      <StatLabel>Modules</StatLabel>
                    </StatItem>
                  </UserStats>
                </TableRow>
              );
            })}
          </LeaderboardTable>
        )}
      </LeaderboardContainer>
    </Layout>
  );
};

export default WorkshopLeaderboard;
